"use client";

import { useEffect, useRef, useState } from "react";
import { useToolUi } from "@/components/tools/tool-ui";
import { formatStopwatch, showsHours } from "@/lib/tool-kit/study/clock-format";

interface Lap {
  number: number;
  lapMs: number;
  totalMs: number;
}

export function stopwatchDocumentTitle(
  elapsedMs: number,
  suffix: string
): string {
  return `${formatStopwatch(elapsedMs)}${suffix}`;
}

/** English strings. A translated content file overrides these through `ui`. */
export const STOPWATCH_STRINGS = {
  unitsLabel: "minutes : seconds . hundredths",
  unitsLabelHours: "hours : minutes : seconds . hundredths",
  resume: "Resume",
  start: "Start",
  pause: "Pause",
  lap: "Lap",
  reset: "Reset",
  laps: "Laps",
  lapColumn: "Lap",
  lapTimeColumn: "Lap time",
  totalTimeColumn: "Total time",
  tabTitleSuffix: " — Stopwatch",
} as const;

export default function StopwatchWidget() {
  const strings = useToolUi(STOPWATCH_STRINGS);
  const [elapsedMs, setElapsedMs] = useState(0);
  const [running, setRunning] = useState(false);
  const [laps, setLaps] = useState<Lap[]>([]);
  const startAtRef = useRef<number | null>(null);
  const originalTitleRef = useRef<string | null>(null);

  useEffect(() => {
    if (!running) {
      return;
    }
    const id = setInterval(() => {
      const startAt = startAtRef.current;
      if (startAt === null) {
        return;
      }
      setElapsedMs(Date.now() - startAt);
    }, 10);
    return () => clearInterval(id);
  }, [running]);

  useEffect(() => {
    originalTitleRef.current = document.title;
    return () => {
      if (originalTitleRef.current !== null) {
        document.title = originalTitleRef.current;
      }
    };
  }, []);

  useEffect(() => {
    if (!running && elapsedMs === 0) {
      if (originalTitleRef.current !== null) {
        document.title = originalTitleRef.current;
      }
      return;
    }
    document.title = stopwatchDocumentTitle(elapsedMs, strings.tabTitleSuffix);
  }, [elapsedMs, running, strings.tabTitleSuffix]);

  function handleStartPause() {
    if (running) {
      setRunning(false);
      startAtRef.current = null;
      return;
    }
    startAtRef.current = Date.now() - elapsedMs;
    setRunning(true);
  }

  function handleLap() {
    if (!running) {
      return;
    }
    const previousTotal = laps.at(-1)?.totalMs ?? 0;
    setLaps([
      ...laps,
      {
        number: laps.length + 1,
        lapMs: elapsedMs - previousTotal,
        totalMs: elapsedMs,
      },
    ]);
  }

  function handleReset() {
    setRunning(false);
    startAtRef.current = null;
    setElapsedMs(0);
    setLaps([]);
  }

  const startLabel = elapsedMs > 0 ? strings.resume : strings.start;

  return (
    <>
      {/* Stopwatch display */}
      <section className="rounded-lg border border-border bg-card p-8 text-center">
        <p className="font-mono font-semibold text-7xl text-foreground tabular-nums md:text-8xl">
          {formatStopwatch(elapsedMs)}
        </p>
        <p className="mt-3 text-muted-foreground text-sm">
          {showsHours(elapsedMs) ? strings.unitsLabelHours : strings.unitsLabel}
        </p>
        <div className="mt-6 flex flex-wrap justify-center gap-3">
          <button
            className="rounded-lg bg-primary px-6 py-2.5 font-medium text-primary-foreground text-sm transition-opacity hover:opacity-90"
            onClick={handleStartPause}
            type="button"
          >
            {running ? strings.pause : startLabel}
          </button>
          <button
            className="rounded-lg border border-border bg-card px-6 py-2.5 font-medium text-foreground text-sm transition-colors hover:bg-muted disabled:opacity-40"
            disabled={!running}
            onClick={handleLap}
            type="button"
          >
            {strings.lap}
          </button>
          <button
            className="rounded-lg border border-border bg-card px-6 py-2.5 font-medium text-foreground text-sm transition-colors hover:bg-muted disabled:opacity-40"
            disabled={elapsedMs === 0}
            onClick={handleReset}
            type="button"
          >
            {strings.reset}
          </button>
        </div>
      </section>

      {/* Laps */}
      {laps.length > 0 && (
        <section className="space-y-3">
          <h2 className="font-semibold text-foreground text-xl">
            {strings.laps}
          </h2>
          <div className="overflow-x-auto rounded-lg border border-border">
            <table className="w-full text-left text-sm">
              <thead>
                <tr className="border-border border-b bg-muted/30">
                  <th className="px-4 py-2.5 font-medium text-foreground">
                    {strings.lapColumn}
                  </th>
                  <th className="px-4 py-2.5 font-medium text-foreground">
                    {strings.lapTimeColumn}
                  </th>
                  <th className="px-4 py-2.5 font-medium text-foreground">
                    {strings.totalTimeColumn}
                  </th>
                </tr>
              </thead>
              <tbody>
                {laps
                  .slice()
                  .reverse()
                  .map((lap) => (
                    <tr
                      className="border-border border-b last:border-b-0"
                      key={lap.number}
                    >
                      <td className="px-4 py-2.5 text-muted-foreground">
                        {lap.number}
                      </td>
                      <td className="px-4 py-2.5 font-mono text-foreground tabular-nums">
                        {formatStopwatch(lap.lapMs)}
                      </td>
                      <td className="px-4 py-2.5 font-mono text-foreground tabular-nums">
                        {formatStopwatch(lap.totalMs)}
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        </section>
      )}
    </>
  );
}
